import type { ActionInput, ActionName } from "@/setup/backend/types";
import { nanoid } from "nanoid";
import { apiFetch } from "./apiFetch";
import type { createApiFetch } from "./apiFetch";

export function createBatchedActionFetch(
	apiFetch: ReturnType<typeof createApiFetch>,
	batchTimeout = 50,
) {
	const batches: Map<
		string,
		{
			calls: {
				name: ActionName;
				input: ActionInput<ActionName>;
				parseOnly?: boolean;
			}[];
			promise: Promise<
				{ name: string; result: { data: any } | { error: string } }[]
			> | null;
		}
	> = new Map();
	let queuedBatchId: string | null = null;

	const dequeueBatch = async (batchId: string) => {
		const batch = batches.get(batchId);
		if (!batch) {
			throw new Error("Batch not found");
		}
		return apiFetch<
			{ name: string; result: { data: any } | { error: string } }[]
		>("/api/actions", "POST", {
			calls: batch.calls,
		});
	};

	return async <T extends ActionName>(
		name: T,
		input: ActionInput<T>,
		batchEnabled = true,
		parseOnly = false,
	) => {
		const batchId = batchEnabled ? queuedBatchId || nanoid() : nanoid();

		if (batchEnabled) {
			if (!queuedBatchId) {
				queuedBatchId = batchId;
				batches.set(batchId, { calls: [], promise: null });
			}
		} else {
			// just give the action a dedicated batch
			batches.set(batchId, { calls: [], promise: null });
		}

		const batch = batches.get(batchId);
		if (!batch) {
			throw new Error("Batch not found");
		}
		batch.calls.push({ name, input, parseOnly: parseOnly });
		const index = batch.calls.length - 1;

		if (!batch.promise) {
			batch.promise = new Promise((resolve, reject) => {
				setTimeout(async () => {
					try {
						queuedBatchId = null;
						const result = await dequeueBatch(batchId);
						resolve(result);
					} catch (error) {
						reject(error);
					} finally {
						batches.delete(batchId);
					}
				}, batchTimeout);
			});
		}

		return batch.promise
			.then((results) => results[index].result)
			.then((result) => {
				if ("error" in result) {
					throw new Error(result.error);
				}
				return result.data;
			});
	};
}

export const actionFetch = createBatchedActionFetch(apiFetch);
